import Dexie, { type Table } from "dexie";
import { emptyDataset, entitySchemas, type Dataset, type EntityType, type SyncOperation } from "@otrack/domain";
import { applyBatch, type LocalStore } from "@otrack/local-store";

interface EntityRow { key: string; type: EntityType; id: string; value: unknown }
interface OutboxRow { seq?: number; opId: string; operation: SyncOperation }
interface MetaRow { key: string; value: unknown }

class OTrackDatabase extends Dexie {
  entities!: Table<EntityRow, string>;
  outbox!: Table<OutboxRow, number>;
  meta!: Table<MetaRow, string>;

  constructor(name: string) {
    super(name);
    this.version(1).stores({
      entities: "key, type",
      outbox: "++seq, &opId",
      meta: "key"
    });
  }
}

const collections: Record<Exclude<EntityType, "settings">, Exclude<keyof Dataset, "settings">> = {
  client: "clients",
  project: "projects",
  task: "tasks",
  timeEntry: "timeEntries",
  invoice: "invoices",
  event: "events"
};

/** IndexedDB store for the browser and the iPhone PWA. */
export class WebLocalStore implements LocalStore {
  private readonly db: OTrackDatabase;
  private readonly channel: BroadcastChannel | null;
  private readonly listeners = new Set<(dataset: Dataset) => void>();
  private dataset: Dataset | null = null;
  private queue: Promise<unknown> = Promise.resolve();

  /** Whether this browser already has a database with this name, without creating one. */
  static exists(databaseName: string): Promise<boolean> {
    return Dexie.exists(databaseName);
  }

  constructor(private readonly databaseName = "otrack") {
    this.db = new OTrackDatabase(databaseName);
    this.channel = typeof BroadcastChannel === "undefined" ? null : new BroadcastChannel(`${databaseName}:changes`);
    if (this.channel) this.channel.onmessage = () => { void this.reloadFromDisk(); };
  }

  async initialize(): Promise<void> {
    await this.db.open();
    await this.load();
  }

  async close(): Promise<void> {
    this.listeners.clear();
    this.channel?.close();
    this.db.close();
  }

  async getDeviceId(): Promise<string> {
    const row = await this.db.meta.get("deviceId");
    if (typeof row?.value === "string") return row.value;
    const deviceId = `device_${crypto.randomUUID()}`;
    await this.db.meta.put({ key: "deviceId", value: deviceId });
    return deviceId;
  }

  async load(): Promise<Dataset> {
    if (this.dataset) return this.dataset;
    this.dataset = await this.readDataset();
    return this.dataset;
  }

  apply(operations: readonly SyncOperation[]): Promise<Dataset> {
    return this.enqueue(() => this.applyNow(operations, true));
  }

  applyRemote(operations: readonly SyncOperation[]): Promise<Dataset> {
    return this.enqueue(() => this.applyNow(operations, false));
  }

  async getCursor(): Promise<number> {
    const row = await this.db.meta.get("cursor");
    return typeof row?.value === "number" ? row.value : 0;
  }

  async setCursor(cursor: number): Promise<void> {
    await this.db.meta.put({ key: "cursor", value: cursor });
  }

  async getValue(key: string): Promise<unknown> {
    return (await this.db.meta.get(`value:${key}`))?.value;
  }

  async setValue(key: string, value: unknown): Promise<void> {
    await this.db.meta.put({ key: `value:${key}`, value });
  }

  /** Serializes writes so concurrent callers never apply against a stale Dataset. */
  private enqueue(task: () => Promise<Dataset>): Promise<Dataset> {
    const run = this.queue.then(task);
    this.queue = run.catch(() => undefined);
    return run;
  }

  subscribe(listener: (dataset: Dataset) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  async getOutbox(limit = 500): Promise<SyncOperation[]> {
    return (await this.db.outbox.orderBy("seq").limit(limit).toArray()).map((row) => row.operation);
  }

  async getPendingOperationCount(): Promise<number> {
    return this.db.outbox.count();
  }

  async acknowledgeOperations(operationIds: readonly string[]): Promise<void> {
    await this.db.outbox.where("opId").anyOf([...operationIds]).delete();
  }

  private async applyNow(operations: readonly SyncOperation[], queueForSync: boolean): Promise<Dataset> {
    const current = await this.load();
    const result = applyBatch(current, operations);
    await this.db.transaction("rw", this.db.entities, this.db.outbox, async () => {
      for (const { type, id } of result.changedEntities) {
        const value = type === "settings" ? result.dataset.settings : result.dataset[collections[type]][id];
        await this.db.entities.put({ key: `${type}:${id}`, type, id, value });
      }
      if (queueForSync && result.accepted.length) await this.db.outbox.bulkAdd(result.accepted.map((operation) => ({ opId: operation.opId, operation })));
    });
    this.dataset = result.dataset;
    this.emit();
    if (result.changedEntities.length) this.channel?.postMessage("changed");
    return this.dataset;
  }

  private async readDataset(): Promise<Dataset> {
    const dataset = emptyDataset(Date.now());
    for (const row of await this.db.entities.toArray()) {
      const parsed = entitySchemas[row.type].safeParse(row.value);
      if (!parsed.success) continue;
      if (row.type === "settings") dataset.settings = parsed.data as Dataset["settings"];
      else (dataset[collections[row.type]] as Record<string, unknown>)[row.id] = parsed.data;
    }
    return dataset;
  }

  private async reloadFromDisk(): Promise<void> {
    this.dataset = await this.readDataset();
    this.emit();
  }

  private emit(): void {
    const dataset = this.dataset;
    if (!dataset) return;
    for (const listener of this.listeners) listener(dataset);
  }
}
